import React from 'react'
import {
    useAuthStore,
    useCCExpireStore,
    useErrorStore,
    useLoadingStore,
    useSmartDetectionStore,
} from '../../state/store'
import RefreshFunctions from '../RefreshFunctions/RefreshFunctions'
import { IoIosCloseCircle } from 'react-icons/io'
import {
    StyledLogoContainer,
    StyledWhite,
    StyledGreen,
    StyledHeading,
    StyledExpiringContainer,
    StyledCloseButton,
    StyledLeftSection,
    StyledRightSection,
} from './styles'
import { AmplitudeEvents } from '../../hooks/amplitude'
import { useAmplitudeTrack } from '../../hooks/amplitude'
import { httpClient } from '../../utils/http'
import RefreshBroker from '../RefreshBroker/RefreshBroker'
import { AxiosError } from 'axios'
import { reevaluateLiveDataEnhanced } from '../../main/smart-detection'
import { useClientConfig } from '../../hooks/useClientConfig'
import { NavLink } from 'react-router-dom'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

const NO_DATA = 'N/A'

// Helper function to format dates as YYYY-MM-DD
const formatDateToYYYYMMDD = (
    dateString: string | null | undefined
): string => {
    if (!dateString) return NO_DATA
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return NO_DATA
        return date.toISOString().split('T')[0]
    } catch (error) {
        return NO_DATA
    }
}

const convertToYahooFormat = (ticker: string) => {
    if (!ticker || ticker.trim().toUpperCase() === 'N/A') {
        return ticker.trim()
    }

    const tickerTrimmed = ticker.trim()

    // Match exchange suffix (e.g., .TO, .V, .AX etc.)
    const match = tickerTrimmed.match(/^(.+?)(\.[A-Z]{1,4})$/)

    if (match) {
        const base = match[1]
        const suffix = match[2]
        // Replace all dots in base with dashes (for Canadian tickers, preferreds etc.)
        const baseConverted = base.replace(/\./g, '-')
        return baseConverted + suffix
    } else {
        // No suffix — check for single dot, usually US class shares like BRK.B
        if ((tickerTrimmed.match(/\./g) || []).length === 1) {
            return tickerTrimmed.replace('.', '-')
        }
        return tickerTrimmed // No changes needed
    }
}

const PageHeader = React.memo(() => {
    const { useSmartDetection: smartDetectionEnabled } = useSmartDetectionStore(
        (state) => state
    )

    const { setSubscriptionCCStatus, status } = useCCExpireStore(
        (state) => state
    )

    const { setLoading } = useLoadingStore((state) => state)
    const { setError } = useErrorStore((state) => state)
    const trackEvent = useAmplitudeTrack()
    const { currentUser } = useAuthStore((state) => state)
    const { branding, isWisesheets } = useClientConfig()

    const aiChatEnabled = useFeatureIsOn('ai-chat')

    const showAiChatButton =
        aiChatEnabled && isWisesheets && currentUser.isSubscribed

    const reevaluateLiveData = async (isAllSheets: boolean) => {
        setLoading({ isLoading: true, message: 'Updating data...' })
        trackEvent(AmplitudeEvents.LIVE_DATA_UPDATED)
        try {
            await Excel.run(async (context) => {
                if (isAllSheets) {
                    const sheets = context.workbook.worksheets

                    sheets.load('items/name')

                    await context.sync()

                    sheets.items.forEach(async function (sheet) {
                        const foundRanges = sheet.findAllOrNullObject(
                            isWisesheets ? 'WISEPRICE' : 'FMPPRICE',
                            {
                                completeMatch: false, // find will match the whole cell value
                                matchCase: false, // find will not match case
                            }
                        )
                        foundRanges.calculate()
                        const foundRangesTTM = sheet.findAllOrNullObject(
                            'TTM',
                            {
                                completeMatch: false, // find will match the whole cell value
                                matchCase: false, // find will not match case
                            }
                        )

                        foundRangesTTM.calculate()
                        const foundRangesFunds = sheet.findAllOrNullObject(
                            isWisesheets ? 'WISEFUNDS' : 'FMPFUNDS',
                            {
                                completeMatch: false, // find will match the whole cell value
                                matchCase: false, // find will not match case
                            }
                        )

                        foundRangesFunds.calculate()

                        if (isWisesheets) {
                            const foundRangesOptions =
                                sheet.findAllOrNullObject(
                                    'WISEOPTIONS', // Find all WISOPTIONS calls
                                    {
                                        completeMatch: false,
                                        matchCase: false,
                                    }
                                )

                            foundRangesOptions.load('areas')
                            const areas = foundRangesOptions.areas
                            areas.load('items')
                            await context.sync()

                            if (foundRangesOptions.areas.items) {
                                // Now filter the loaded ranges
                                const rangesToCalculate =
                                    foundRangesOptions.areas.items.filter(
                                        (range) => {
                                            const formula = range
                                                .formulas[0][0] as string

                                            return (
                                                formula.split(',').length ===
                                                    3 &&
                                                formula.includes('WISEOPTIONS')
                                            )
                                        }
                                    )

                                // Calculate filtered ranges
                                if (rangesToCalculate.length > 0) {
                                    rangesToCalculate.forEach((range) =>
                                        range.calculate()
                                    )
                                }
                            }
                        }
                    })
                } else {
                    // create the new sheet
                    const currentSheet =
                        context.workbook.worksheets.getActiveWorksheet()
                    const foundRanges = currentSheet.findAllOrNullObject(
                        isWisesheets ? 'WISEPRICE' : 'FMPPRICE',
                        {
                            completeMatch: false, // find will match the whole cell value
                            matchCase: false, // find will not match case
                        }
                    )
                    foundRanges.calculate()
                    const foundRangesTTM = currentSheet.findAllOrNullObject(
                        'TTM',
                        {
                            completeMatch: false, // find will match the whole cell value
                            matchCase: false, // find will not match case
                        }
                    )

                    foundRangesTTM.calculate()
                    const foundRangesFunds = currentSheet.findAllOrNullObject(
                        isWisesheets ? 'WISEFUNDS' : 'FMPFUNDS',
                        {
                            completeMatch: false, // find will match the whole cell value
                            matchCase: false, // find will not match case
                        }
                    )

                    foundRangesFunds.calculate()

                    if (isWisesheets) {
                        const foundRangesOptions =
                            currentSheet.findAllOrNullObject(
                                'WISEOPTIONS', // Find all WISOPTIONS calls
                                {
                                    completeMatch: false,
                                    matchCase: false,
                                }
                            )

                        foundRangesOptions.load('areas')
                        const areas = foundRangesOptions.areas
                        areas.load('items')
                        await context.sync()

                        if (foundRangesOptions.areas.items) {
                            // Now filter the loaded ranges
                            const rangesToCalculate =
                                foundRangesOptions.areas.items.filter(
                                    (range) => {
                                        const formula = range
                                            .formulas[0][0] as string

                                        return (
                                            formula.split(',').length === 3 &&
                                            formula.includes('WISEOPTIONS')
                                        )
                                    }
                                )

                            // Calculate filtered ranges
                            if (rangesToCalculate.length > 0) {
                                rangesToCalculate.forEach((range) =>
                                    range.calculate()
                                )
                            }
                        }
                    }
                }

                await context.sync()
            })
        } catch (error) {
            setError({
                hasError: true,
                errorMessage: 'Something went wrong. Please try again.',
            })
        } finally {
            setLoading({ isLoading: false, message: '' })
        }
    }

    const refreshErrors = async () => {
        setLoading({ isLoading: true, message: 'Refreshing errors...' })
        trackEvent(AmplitudeEvents.REFRESH_ERRORS)
        try {
            await Excel.run(async (context) => {
                // create the new sheet
                var currentSheet =
                    context.workbook.worksheets.getActiveWorksheet()

                // Find and replace _xlduf_ in formulas
                const foundRangesXlduf = currentSheet.findAllOrNullObject(
                    '_xlduf_',
                    {
                        completeMatch: false,
                        matchCase: false,
                    }
                )

                if (foundRangesXlduf.areas) {
                    foundRangesXlduf.areas.load('items')
                    await context.sync()

                    if (foundRangesXlduf.areas.items) {
                        foundRangesXlduf.areas.items.forEach((range) => {
                            const formula = range.formulas[0][0] as string
                            if (formula) {
                                // Keep the equals sign, remove everything between it and _xlduf_
                                const newFormula = formula.replace(
                                    /(=).*?_xlduf_/g,
                                    '$1'
                                )
                                range.formulas = [[newFormula]]
                            }
                        })
                    }
                }

                var foundRanges = currentSheet.findAllOrNullObject(
                    'Too many simultaneous requests.',
                    {
                        completeMatch: false, // find will match the whole cell value
                        matchCase: false, // find will not match case
                    }
                )
                foundRanges.calculate()

                var foundRanges2 = currentSheet.findAllOrNullObject('#VALUE!', {
                    completeMatch: false, // find will match the whole cell value
                    matchCase: false, // find will not match case
                })
                foundRanges2.calculate()

                var foundRanges3 = currentSheet.findAllOrNullObject(
                    'Unexpected result. Verify your function call and retry.',
                    {
                        completeMatch: false, // find will match the whole cell value
                        matchCase: false, // find will not match case
                    }
                )
                foundRanges3.calculate()

                var foundRanges4 = currentSheet.findAllOrNullObject('#NAME?', {
                    completeMatch: false, // find will match the whole cell value
                    matchCase: false, // find will not match case
                })

                foundRanges4.calculate()

                var foundRanges5 = currentSheet.findAllOrNullObject(
                    'Unexpected error. Try function with multiple tickers.',
                    {
                        completeMatch: false, // find will match the whole cell value
                        matchCase: false, // find will not match case
                    }
                )
                foundRanges5.calculate()

                await context.sync()
            })
        } catch (error) {
            setError({
                hasError: true,
                errorMessage: 'Something went wrong. Please try again.',
            })
        } finally {
            setLoading({ isLoading: false, message: '' })
        }
    }

    const refreshSnapTrade = async () => {
        if (!currentUser.isSubscribed) {
            setError({
                hasError: true,
                errorMessage:
                    'Upgrade to our Pro plan for access. <a href="https://www.wisesheets.io/payment?plan=PRO">Upgrade.</a>',
            })
            return
        }

        setLoading({ isLoading: true, message: 'Syncing...' })
        trackEvent(AmplitudeEvents.REFRESH_BROKER)

        try {
            const response = await httpClient.post(
                '/api/private/snaptrade/holdings'
            )

            const holdings = response.data

            const dataToWrite = [
                [
                    'Account',
                    'Symbol',
                    'Options Contract',
                    'Expiry',
                    'Company name',
                    'Exchange',
                    'Currency',
                    'Asset type',
                    'Units held',
                    'Average purchase price',
                    'Current price',
                    'Total cost',
                    'Market value',
                    'Unrealized P&L',
                    'Unrealized P&L (%)',
                ],
            ]

            for (let i = 0; i < holdings.length; i++) {
                const holding = holdings[i]

                let row = []

                const positions = holding.positions

                if (positions && positions.length > 0) {
                    for (let j = 0; j < positions.length; j++) {
                        const position = positions[j]

                        if (!position) continue

                        const units = position.units || 0
                        const avgPrice = position.average_purchase_price || 0
                        const currentPrice = position.price || 0

                        const totalCost = units * avgPrice
                        const marketValue = units * currentPrice
                        const unrealisedPL = marketValue - totalCost
                        const unrealisedPLPercentage =
                            totalCost !== 0 ? unrealisedPL / totalCost : 0

                        row.push(
                            holding.account_name ||
                                holding.account?.name ||
                                NO_DATA
                        )
                        row.push(
                            convertToYahooFormat(
                                position.symbol?.symbol?.symbol || NO_DATA
                            )
                        )
                        row.push(NO_DATA)
                        row.push(NO_DATA)
                        row.push(
                            position.symbol?.symbol?.description || NO_DATA
                        )
                        row.push(
                            position.symbol?.symbol?.exchange?.code || NO_DATA
                        )
                        row.push(position.currency?.code || NO_DATA)
                        row.push(
                            position.symbol?.symbol?.type?.description ||
                                NO_DATA
                        )
                        row.push(units)
                        row.push(avgPrice) // J
                        row.push(currentPrice) // K
                        row.push(totalCost) // L
                        row.push(marketValue) // M
                        row.push(unrealisedPL) // N
                        row.push(unrealisedPLPercentage) // O

                        dataToWrite.push(row)
                        row = []
                    }
                }

                const optionPositions = holding.option_positions

                if (optionPositions && optionPositions.length > 0) {
                    for (let j = 0; j < optionPositions.length; j++) {
                        const position = optionPositions[j]

                        if (!position) continue

                        const units = position.units || 0
                        const avgPrice = position.average_purchase_price || 0
                        const currentPrice = position.price || 0

                        const totalCost = units * avgPrice
                        const marketValue = units * currentPrice
                        const unrealisedPL = marketValue - totalCost
                        const unrealisedPLPercentage =
                            totalCost !== 0 ? unrealisedPL / totalCost : 0

                        const optionTicker =
                            position.symbol?.option_symbol?.ticker
                        const tickerParts = optionTicker
                            ? optionTicker.split(' ')
                            : []
                        const optionContract =
                            tickerParts.length > 1
                                ? tickerParts[tickerParts.length - 1]
                                : NO_DATA
                        row.push(
                            holding.account_name ||
                                holding.account?.name ||
                                NO_DATA
                        )
                        row.push(
                            convertToYahooFormat(
                                position.symbol?.option_symbol
                                    ?.underlying_symbol?.symbol || NO_DATA
                            )
                        )
                        row.push(optionContract)
                        row.push(
                            position.symbol?.option_symbol?.expiration_date ||
                                NO_DATA
                        )
                        row.push(
                            position.symbol?.option_symbol?.underlying_symbol
                                ?.description || NO_DATA
                        )
                        row.push(
                            position.symbol?.option_symbol?.underlying_symbol
                                ?.exchange?.code || NO_DATA
                        )
                        row.push(position.currency?.code || NO_DATA)
                        row.push(
                            position.symbol?.option_symbol?.underlying_symbol
                                ?.description || NO_DATA
                        )
                        row.push(units)
                        row.push(avgPrice) // J
                        row.push(currentPrice) // K
                        row.push(totalCost) // L
                        row.push(marketValue) // M
                        row.push(unrealisedPL) // N
                        row.push(unrealisedPLPercentage) // O

                        dataToWrite.push(row)
                        row = []
                    }
                }
            }

            if (dataToWrite.length === 0) {
                setError({
                    hasError: true,
                    errorMessage:
                        'No broker data found. Please connect your broker.',
                })
                return
            }

            await Excel.run(async (context) => {
                const currentSheet =
                    context.workbook.worksheets.getActiveWorksheet()
                const dataToWriteRange = currentSheet
                    .getRange('A1')
                    .getResizedRange(
                        dataToWrite.length - 1,
                        dataToWrite[0].length - 1
                    )
                dataToWriteRange.values = dataToWrite

                const accountRow = currentSheet
                    .getRange('A1')
                    .getResizedRange(0, dataToWrite[0].length - 1)
                accountRow.format.fill.color = '#364165'
                accountRow.format.font.color = '#F7F8F8'
                accountRow.format.font.bold = true

                currentSheet.autoFilter.apply(dataToWriteRange)
                dataToWriteRange.format.autofitColumns()

                const purhcasePrice = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`J2:J${dataToWrite[0].length + 1}`)
                purhcasePrice.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const price = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`K2:K${dataToWrite[0].length + 1}`)
                price.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const totalCost = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`L2:L${dataToWrite[0].length + 1}`)
                totalCost.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const marketValue = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`M2:M${dataToWrite[0].length + 1}`)
                marketValue.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const unrealisedPL = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`N2:N${dataToWrite[0].length + 1}`)
                unrealisedPL.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const unrealisedPLPercentage = context.workbook.worksheets
                    .getActiveWorksheet()
                    .getRange(`O2:O${dataToWrite[0].length + 1}`)
                unrealisedPLPercentage.numberFormat = [['0.00%_);(0.00%)']]

                await context.sync()
            })
        } catch (error) {
            console.log(error)
            if (error instanceof AxiosError) {
                const errorMsg =
                    typeof error.response?.data === 'string'
                        ? error.response.data
                        : 'Something unexpected happened. Please let us know and we will fix it.'
                setError({
                    hasError: true,
                    errorMessage: errorMsg,
                })
            } else {
                setError({
                    hasError: true,
                    errorMessage: 'Something went wrong. Please try again.',
                })
            }
        } finally {
            setLoading({ isLoading: false, message: '' })
        }
    }

    const refreshSnapTradeTransactions = async () => {
        if (!currentUser.isSubscribed) {
            setError({
                hasError: true,
                errorMessage:
                    'Upgrade to our Pro plan for access. <a href="https://www.wisesheets.io/payment?plan=PRO">Upgrade.</a>',
            })
            return
        }
        setLoading({ isLoading: true, message: 'Syncing...' })
        trackEvent(AmplitudeEvents.REFRESH_TRANSACTIONS)

        try {
            const response = await httpClient.get(
                '/api/private/snaptrade/transactions'
            )
            const transactions = response.data

            const dataToWrite = [
                [
                    'Account',
                    'Trade Date',
                    'Settlement Date',
                    'Status',
                    'Type',
                    'Option Type',
                    'Symbol',
                    'Description',
                    'Option Ticker',
                    'Underlying Symbol',
                    'Strike Price',
                    'Expiration Date',
                    'Price',
                    'Fee',
                    'Currency',
                    'FX Rate',
                    'Amount',
                    'Institution',
                    'Profit/Loss',
                ],
            ]

            for (let i = 0; i < transactions.length; i++) {
                const transaction = transactions[i]

                const settlementDate = transaction.settlement_date
                    ? new Date(transaction.settlement_date)
                    : null
                const currentDate = new Date()
                const status =
                    settlementDate && settlementDate < currentDate
                        ? 'Settled'
                        : 'Pending'

                const marketValue =
                    (transaction.price || 0) * (transaction.units || 0)
                const profitLoss = marketValue - (transaction.amount || 0)

                dataToWrite.push([
                    transaction.account_name || NO_DATA,
                    formatDateToYYYYMMDD(transaction.trade_date),
                    formatDateToYYYYMMDD(transaction.settlement_date),
                    status,
                    transaction.type || NO_DATA,
                    transaction.option_type || NO_DATA,
                    transaction.symbol?.symbol || NO_DATA,
                    transaction.description || NO_DATA,
                    transaction.option_symbol?.ticker || NO_DATA,
                    transaction.option_symbol?.underlying_symbol?.symbol ||
                        NO_DATA,
                    transaction.option_symbol?.strike_price || NO_DATA,
                    formatDateToYYYYMMDD(
                        transaction.option_symbol?.expiration_date
                    ),
                    transaction.price || 0,
                    transaction.fee || 0,
                    transaction.currency?.code || NO_DATA,
                    transaction.fx_rate || NO_DATA,
                    transaction.amount || 0,
                    transaction.institution || NO_DATA,
                    profitLoss,
                ])
            }

            if (dataToWrite.length === 1) {
                setError({
                    hasError: true,
                    errorMessage: 'No transaction data found.',
                })
                return
            }

            await Excel.run(async (context) => {
                const currentSheet =
                    context.workbook.worksheets.getActiveWorksheet()
                const dataToWriteRange = currentSheet
                    .getRange('A1')
                    .getResizedRange(
                        dataToWrite.length - 1,
                        dataToWrite[0].length - 1
                    )
                dataToWriteRange.values = dataToWrite

                const headerRow = currentSheet
                    .getRange('A1')
                    .getResizedRange(0, dataToWrite[0].length - 1)
                headerRow.format.fill.color = '#364165'
                headerRow.format.font.color = '#F7F8F8'
                headerRow.format.font.bold = true

                currentSheet.autoFilter.apply(dataToWriteRange)
                dataToWriteRange.format.autofitColumns()

                const priceColumn = currentSheet.getRange(
                    `M2:M${dataToWrite.length}`
                )
                priceColumn.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const feeColumn = currentSheet.getRange(
                    `N2:N${dataToWrite.length}`
                )
                feeColumn.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const amountColumn = currentSheet.getRange(
                    `Q2:Q${dataToWrite.length}`
                )
                amountColumn.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                const profitLossColumn = currentSheet.getRange(
                    `S2:S${dataToWrite.length}`
                )
                profitLossColumn.numberFormat = [['$#,##0.00_);($#,##0.00)']]

                await context.sync()
            })
        } catch (error) {
            console.log(error)
            if (error instanceof AxiosError) {
                const errorMsg =
                    typeof error.response?.data === 'string'
                        ? error.response.data
                        : 'Something unexpected happened. Please let us know and we will fix it.'
                setError({
                    hasError: true,
                    errorMessage: errorMsg,
                })
            } else {
                setError({
                    hasError: true,
                    errorMessage: 'Something went wrong. Please try again.',
                })
            }
        } finally {
            setLoading({ isLoading: false, message: '' })
        }
    }

    return (
        <>
            <StyledHeading>
                <StyledLeftSection>
                    {isWisesheets ? (
                        <RefreshBroker
                            refreshBroker={refreshSnapTrade}
                            refreshTransactions={refreshSnapTradeTransactions}
                        />
                    ) : null}
                    {showAiChatButton ? (
                        <StyledLogoContainer as={NavLink} to="/feature/chat">
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.5003 12.9375H14.6253V17.4375H13.5003V12.9375ZM15.7503 11.8125H16.8753V17.4375H15.7503V11.8125ZM11.2503 15.1875H12.3753V17.4375H11.2503V15.1875ZM10.1253 11.25V10.125H10.6878V6.1875H10.1253V5.0625H12.3753V6.1875H11.8128V10.125H12.3753V11.25H10.1253ZM8.15652 11.25H9.28152L7.31277 5.0625H5.62527L3.6582 11.25H4.7832L5.12127 10.125H7.80889L8.15652 11.25ZM5.45989 9L6.37564 5.95631L6.51964 5.95463L7.46127 9H5.45989Z"
                                    fill="#48C44F"
                                />
                                <path
                                    d="M9.5625 16.875H0V0H16.875V9.5625H15.75V1.125H1.125V15.75H9.5625V16.875Z"
                                    fill="#48C44F"
                                />
                            </svg>
                        </StyledLogoContainer>
                    ) : null}
                </StyledLeftSection>
                {!isWisesheets ? (
                    <StyledWhite>{branding.companyName}</StyledWhite>
                ) : (
                    <StyledWhite>
                        Wise<StyledGreen>sheets</StyledGreen>
                    </StyledWhite>
                )}
                <StyledRightSection>
                    <RefreshFunctions
                        refreshFunctions={
                            smartDetectionEnabled && isWisesheets
                                ? reevaluateLiveDataEnhanced
                                : reevaluateLiveData
                        }
                        refreshErrors={refreshErrors}
                    />
                </StyledRightSection>
            </StyledHeading>
            {status.expiring ? (
                <StyledExpiringContainer>
                    <div>⚠️ Your plan is expiring soon</div>
                    <a
                        href="https://wisesheets.io/manage-account"
                        target="_blank"
                    >
                        Update billing
                    </a>
                    <StyledCloseButton
                        onClick={() => setSubscriptionCCStatus(false)}
                    >
                        <IoIosCloseCircle size={24} />
                    </StyledCloseButton>
                </StyledExpiringContainer>
            ) : null}
        </>
    )
})

PageHeader.displayName = 'PageHeader'

export default PageHeader
